import { GET_PILLS_TO_RENDER_TIME_LIMIT } from './constants';
import ArbitratedSmartPills from '../store/schema/ArbitratedSmartPills';
import SmartPillArbitrator from '../store/schema/SmartPillArbitrator';
import SmartPillBuilder from '../store/schema/SmartPillBuilder';
import SmartPillData from '../store/schema/SmartPillData';
import SmartPillPreRunner from '../store/schema/SmartPillPreRunner';
import SmartPillLogDataUpdater from '../store/schema/SmartPillLogDataUpdater';
import logUnsampledDatapoint from './logUnsampledDatapoint';
import { PerformanceDatapoint } from 'owa-analytics';

interface FeatureToErrorMap {
    [key: string]: Error;
}

/**
 * Executes all smart pill builders and returns the arbitrated result
 * @param builders: Array of pill builders
 * @param arbitrator: Arbitrator to run after pill builders complete
 * @param preRunners: Array of SmartPillPreRunners to execute (before pill builders execute)
 * @param logDataUpdaters: Optional methods to run after pills are built but before they are arbitrated
 * Note: Method should update the SmartPillLogData of all built pills. Useful if metadata needs to be applied to pills before
 * they are arbitrated.
 * @param timeout: Optional time limit (ms) for pill builders to run (default is 2500)
 */
export default async function getPillsToRender(
    builders: SmartPillBuilder[],
    arbitrator: SmartPillArbitrator,
    preRunners?: SmartPillPreRunner[],
    logDataUpdaters?: SmartPillLogDataUpdater[],
    timeout?: number
): Promise<ArbitratedSmartPills> {
    const errorMap: FeatureToErrorMap = {};
    const preDefinedTimeout = timeout || GET_PILLS_TO_RENDER_TIME_LIMIT;

    // Run any preRunners first.
    if (preRunners) {
        await Promise.all(
            preRunners.map(preRunner =>
                preRunner.preRun().catch(error => {
                    // Catch the error and log it later.
                    errorMap[preRunner.getName()] = new Error(error);
                })
            )
        );
    }

    // Measure how long it takes for the pill builders to execute.
    const performanceDatapoint = new PerformanceDatapoint('SmartPillPerformance');
    performanceDatapoint.addCustomProperty('preDefinedTimeout', preDefinedTimeout);

    // Current strategy is to dispatch all pill builders and await their results.
    // We ignore the results of any pill builders that take too long to execute.
    const pills: SmartPillData[][] = await Promise.all(
        builders.map(b =>
            Promise.race([b.buildPills(), createTimeoutPromise(preDefinedTimeout)]).catch(error => {
                // If any pill builder rejects, throws an exception, or timesout, we need to catch the error to ensure
                // Promise.all doesn't reject prematurely.
                errorMap[b.getName()] = new Error(error);
                return [];
            })
        )
    ).then(val => {
        performanceDatapoint.end();
        return val;
    });

    // We have an array of arrays of SmartPillData objects; flatten it.
    const flattenedPills: SmartPillData[] = [].concat.apply([], pills);
    let flattenedPillCount: number = flattenedPills.length;

    // After creating the pills, but before we arbitrate the pills, run any logDataUpdaters.
    if (logDataUpdaters) {
        await Promise.all(
            logDataUpdaters.map(logDataUpdater =>
                logDataUpdater.updateLogData(flattenedPills).catch(error => {
                    // Catch the error and log it later.
                    errorMap[logDataUpdater.getName()] = new Error(error);
                })
            )
        );
    }

    // Now arbitrate and return the pills to show and hide.
    let arbitratedSmartPills: ArbitratedSmartPills = {
        pillsToShow: [],
        pillsToHide: [],
        arbitratorId: '',
    };

    try {
        arbitratedSmartPills = arbitrator.arbitratePills(flattenedPills);
    } catch (error) {
        errorMap['arbitrator'] = new Error(error);
    }

    // Log a datapoint if there are any errors.
    const errorMapKeys: string[] = Object.keys(errorMap);
    if (errorMapKeys.length > 0) {
        const knownErrors: string[] = [];
        const unknownErrors: string[] = [];

        errorMapKeys.forEach(errorKey => {
            const error = errorMap[errorKey];
            if (error.hasOwnProperty('networkError')) {
                knownErrors.push(errorKey + '_' + error.message);
            } else {
                unknownErrors.push(errorKey + '_' + error.message);
            }
        });

        // owa_1 consists of errors that we knowingly threw.
        // owa_2 consists of errors that we unknowingly threw.
        // owa_3 is how many pills we got from the pill builders.
        logUnsampledDatapoint('SmartPillError', [
            knownErrors.toString(),
            unknownErrors.toString(),
            flattenedPillCount,
        ]);
    }

    return arbitratedSmartPills;
}

/**
 * Creates a promise that rejects after running for timeoutNum milliseconds.
 */
function createTimeoutPromise(timeoutNum: number): Promise<SmartPillData[]> {
    return new Promise((resolve, reject) => {
        setTimeout(function() {
            const timeoutError: Error = new Error('timeout');
            (timeoutError as { networkError?: boolean }).networkError = true;
            reject(timeoutError);
        }, timeoutNum);
    });
}
